import { ErrorMessage, Field as FormField, Label } from '../../catalyst/fieldset'
import { Input } from '../../catalyst/input'
import { Textarea } from '../../catalyst/textarea'
import { api } from '../../lib/api'
import { amountProblem, optText, str, type StepCtx } from '../flow'

/** The most recent entry of a per-field log (this year, else last year), or null. */
export async function lastFieldLog<T>(path: string, orgId: string, farmId: string, fieldId: string | undefined): Promise<T | null> {
  if (!fieldId) return null
  const year = new Date().getFullYear()
  for (const seasonYear of [year, year - 1]) {
    const { data } = await api.getPage<T>(`/orgs/${orgId}/farms/${farmId}/${path}?fieldId=${fieldId}&seasonYear=${seasonYear}&limit=1`)
    if (data[0]) return data[0]
  }
  return null
}


/** The fields an entry covers, for a review title: " · Field A" or " · Field A, Field B". */
export const placeOf = ({ selectedFields }: Pick<StepCtx, 'selectedFields'>) => (selectedFields.length ? ` · ${selectedFields.map((f) => f.name).join(', ')}` : '')

export const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/** A review line, left out when the value is blank. */
export const line = (label: string, value: unknown) => (optText(value) ? [{ label, value: str(value) }] : [])

/** A farm-level log's records page. */
export const farmPath = (path: string) => (farmId: string) => `/farms/${farmId}/${path}`

/** A text input bound to one wizard value. */
export function TextField({ ctx, label, valueKey, multiline, type }: { ctx: StepCtx; label: string; valueKey: string; multiline?: boolean; type?: string }) {
  return (
    <FormField>
      <Label>{label}</Label>
      {multiline ? (
        <Textarea rows={3} value={str(ctx.values[valueKey])} onChange={(e) => ctx.patch({ [valueKey]: e.target.value })} />
      ) : (
        <Input type={type} value={str(ctx.values[valueKey])} onChange={(e) => ctx.patch({ [valueKey]: e.target.value })} autoComplete="off" />
      )}
    </FormField>
  )
}

/**
 * A number box for an amount or rate, inside a Catalyst Field: it marks itself invalid and says why
 * as soon as the number can't be saved (not a number, zero, or too large to store).
 */
export function AmountInput({ value, onChange }: { value: unknown; onChange: (value: string) => void }) {
  const problem = amountProblem(value)
  return (
    <>
      <Input type="number" inputMode="decimal" min="0" step="any" invalid={problem !== null} value={str(value)} onChange={(e) => onChange(e.target.value)} />
      {problem && <ErrorMessage>{problem}</ErrorMessage>}
    </>
  )
}
